import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { AuthLayout } from "./AuthLayout";
import { Alert, Button, Field, Input } from "../components/ui";

export function Signup() {
  const navigate = useNavigate();
  const { setMe } = useAuth();

  const [form, setForm] = useState({
    orgName: "",
    displayName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [error, setError] = useState<ApiError | string | null>(null);
  const [busy, setBusy] = useState(false);

  const set = (key: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  // Field-level messages come straight from the API's `issues` array, so the
  // rules live in one place rather than being restated in the client and
  // drifting out of sync with them.
  const fieldError = (field: string) =>
    error instanceof ApiError ? error.fieldError(field) : undefined;

  const topError =
    error instanceof ApiError
      ? error.issues.length === 0
        ? error.message
        : null
      : error;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const { user, organization } = await api.signup({
        orgName: form.orgName,
        ...(form.displayName.trim() ? { displayName: form.displayName } : {}),
        email: form.email,
        password: form.password,
        confirmPassword: form.confirmPassword,
      });
      setMe({ user, organization });
      navigate("/app", { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err : "Something went wrong.");
      setBusy(false);
    }
  }

  return (
    <AuthLayout>
      <div className="mb-8">
        <h1 className="text-[2rem] leading-tight font-bold tracking-tight">Set up your fleet</h1>
        <p className="mt-1.5 text-[1rem] text-ink-soft">One account for your organisation</p>
      </div>

      <form onSubmit={onSubmit} className="flex flex-col gap-5" noValidate>
        {topError && <Alert>{topError}</Alert>}

        <Field label="Company name" error={fieldError("orgName")}>
          {(id) => (
            <Input
              id={id}
              autoFocus
              required
              value={form.orgName}
              onChange={set("orgName")}
              invalid={!!fieldError("orgName")}
              placeholder="Serenity Aviation"
            />
          )}
        </Field>

        <Field label="Your name" error={fieldError("displayName")}>
          {(id) => <Input id={id} value={form.displayName} onChange={set("displayName")} placeholder="Optional" />}
        </Field>

        <Field label="Email" error={fieldError("email")}>
          {(id) => (
            <Input
              id={id}
              type="email"
              autoComplete="username"
              required
              value={form.email}
              onChange={set("email")}
              invalid={!!fieldError("email")}
              placeholder="ops@youroperation.com"
            />
          )}
        </Field>

        <Field
          label="Password"
          hint="At least 12 characters."
          error={fieldError("password")}
        >
          {(id) => (
            <Input
              id={id}
              type="password"
              autoComplete="new-password"
              required
              value={form.password}
              onChange={set("password")}
              invalid={!!fieldError("password")}
            />
          )}
        </Field>

        <Field label="Confirm password" error={fieldError("confirmPassword")}>
          {(id) => (
            <Input
              id={id}
              type="password"
              autoComplete="new-password"
              required
              value={form.confirmPassword}
              onChange={set("confirmPassword")}
              invalid={!!fieldError("confirmPassword")}
            />
          )}
        </Field>

        <Button type="submit" busy={busy}>
          Create organisation
        </Button>
      </form>

      <p className="mt-8 text-[0.9rem] text-ink-soft">
        Already set up?{" "}
        <Link to="/login" className="font-semibold text-accent-text hover:underline underline-offset-4">
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
}
